import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { tenants as tenantsApi } from '../api/client.js';
import { useTenant } from '../context/TenantContext.js';
import { useToast } from '../components/Toast.js';

const STEPS = ['Server Name', 'Guild ID', 'Bot Token', 'Solana Wallet', 'Review'];
const BASE58_REGEX = /^[123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz]+$/;
const STORAGE_KEY = 'subfi_onboarding';

function validateGuildId(v: string): string | null {
  if (!v) return 'Guild ID is required';
  if (!/^\d{17,20}$/.test(v)) return 'Must be a 17-20 digit number';
  return null;
}

function validateBotToken(v: string): string | null {
  if (!v) return 'Bot token is required';
  if (v.length < 50) return 'Token must be at least 50 characters';
  return null;
}

function validateWallet(v: string): string | null {
  if (!v) return 'Wallet address is required';
  if (v.length < 32 || v.length > 44) return 'Must be 32-44 characters';
  if (!BASE58_REGEX.test(v)) return 'Must contain only base58 characters';
  return null;
}

function loadSavedProgress(): { step: number; name: string; guildId: string; botToken: string; walletSolana: string } | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    return {
      step: typeof data.step === 'number' ? data.step : 0,
      name: data.name ?? '',
      guildId: data.guildId ?? '',
      botToken: data.botToken ?? '',
      walletSolana: data.walletSolana ?? '',
    };
  } catch {
    return null;
  }
}

export function OnboardingPage() {
  const navigate = useNavigate();
  const { refreshTenants, hasServer } = useTenant();
  const { toast } = useToast();

  const saved = loadSavedProgress();

  const [step, setStep] = useState(saved?.step ?? 0);
  const [name, setName] = useState(saved?.name ?? '');
  const [guildId, setGuildId] = useState(saved?.guildId ?? '');
  const [botToken, setBotToken] = useState(saved?.botToken ?? '');
  const [walletSolana, setWalletSolana] = useState(saved?.walletSolana ?? '');
  const [showToken, setShowToken] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');

  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const touch = (field: string) => setTouched((p) => ({ ...p, [field]: true }));

  // Persist progress to localStorage
  const saveProgress = useCallback(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ step, name, guildId, botToken, walletSolana }));
    } catch { /* storage full or unavailable */ }
  }, [step, name, guildId, botToken, walletSolana]);

  useEffect(() => {
    saveProgress();
  }, [saveProgress]);

  const guildErr = validateGuildId(guildId);
  const tokenErr = validateBotToken(botToken);
  const walletErr = validateWallet(walletSolana);

  const canNext = (): boolean => {
    switch (step) {
      case 0: return name.trim().length > 0;
      case 1: return !guildErr;
      case 2: return !tokenErr;
      case 3: return !walletErr;
      default: return true;
    }
  };

  const handleCreate = async () => {
    setCreating(true);
    setError('');
    try {
      await tenantsApi.create({
        name: name.trim(),
        guildId: guildId.trim(),
        botToken: botToken.trim() || undefined,
        walletSolana: walletSolana.trim() || undefined,
      });
      localStorage.removeItem(STORAGE_KEY);
      await refreshTenants();
      navigate('/');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to create server';
      setError(msg);
      setCreating(false);
    }
  };

  const handleSkip = () => {
    // Clear optional fields and jump to review
    if (step <= 1) {
      // From steps 0-1, skip means jump to review with just what's filled
      if (!name.trim() || guildErr) return;
    }
    if (step <= 2) setBotToken('');
    if (step <= 3) setWalletSolana('');
    setStep(4);
  };

  // Quick-create: just name + guild ID, skip everything else
  const handleQuickCreate = async () => {
    if (!name.trim() || guildErr) return;
    setCreating(true);
    setError('');
    try {
      await tenantsApi.create({ name: name.trim(), guildId: guildId.trim() });
      localStorage.removeItem(STORAGE_KEY);
      await refreshTenants();
      toast('Server created — configure the rest in Settings');
      navigate('/');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to create server';
      setError(msg);
      setCreating(false);
    }
  };

  const inputClass = (field: string, errMsg: string | null): string => {
    if (!touched[field]) return 'form-input';
    return `form-input ${errMsg ? 'invalid' : 'valid'}`;
  };

  // Can skip on steps 2+ (bot token and wallet) since those are configurable later
  // Also allow skipping from step 1 if name is already filled
  const canSkip = step === 2 || step === 3;

  return (
    <div className="onboarding-page">
      <div className="onboarding-card">
        <div className="brand" style={{ justifyContent: 'center', marginBottom: 24 }}>
          <div className="brand-mark" />
          <span className="brand-name">SubFi Pay</span>
        </div>

        {/* Step indicator */}
        <div className="onboarding-steps">
          {STEPS.map((label, i) => (
            <div
              key={label}
              className={`onboarding-step ${i === step ? 'active' : ''} ${i < step ? 'completed' : ''}`}
              title={label}
            >
              {i < step ? (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" style={{ width: 12, height: 12 }}>
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              ) : (
                i + 1
              )}
            </div>
          ))}
        </div>

        {/* Step 0: Server Name */}
        {step === 0 && (
          <div className="step-transition">
            <h1 className="login-title">Set Up Your Server</h1>
            <p className="login-subtitle" style={{ marginBottom: 24 }}>
              Let's get your Discord server connected to SubFi Pay so you can start accepting crypto payments.
            </p>
            <div className="form-group">
              <label className="form-label">Server Name</label>
              <input
                className="form-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. My Community"
                autoFocus
              />
              <div className="form-hint">
                This is just a display name for your dashboard. It doesn't need to match your Discord server name exactly.
              </div>
            </div>
          </div>
        )}

        {/* Step 1: Guild ID */}
        {step === 1 && (
          <div className="step-transition">
            <h1 className="login-title">Your Server's ID</h1>
            <p className="login-subtitle" style={{ marginBottom: 20 }}>
              Every Discord server has a unique numeric ID. Here's how to find yours:
            </p>

            <div style={{ background: 'var(--s2)', borderRadius: 10, padding: 14, marginBottom: 20, fontSize: 13, color: 'var(--t2)', lineHeight: 1.6, border: '1px solid var(--b1)' }}>
              <div style={{ fontWeight: 600, marginBottom: 8, color: 'var(--t1)' }}>How to find your Guild ID:</div>
              <div>1. Open Discord and go to <strong>User Settings</strong> (gear icon)</div>
              <div>2. Go to <strong>Advanced</strong> and enable <strong>Developer Mode</strong></div>
              <div>3. Close settings, then <strong>right-click your server name</strong> in the sidebar</div>
              <div>4. Click <strong>Copy Server ID</strong> and paste it below</div>
            </div>

            <div className="form-group">
              <label className="form-label">Guild ID</label>
              <input
                className={inputClass('guildId', guildErr)}
                value={guildId}
                onChange={(e) => setGuildId(e.target.value)}
                onBlur={() => touch('guildId')}
                placeholder="e.g. 123456789012345678"
                style={{ fontFamily: 'monospace' }}
                autoFocus
              />
              {touched.guildId && guildErr && (
                <div className="form-hint error">{guildErr}</div>
              )}
              {touched.guildId && !guildErr && guildId && (
                <div className="form-hint success">Valid guild ID</div>
              )}
              <div className="form-hint" style={{ marginTop: 6 }}>
                It should be a long number, like 123456789012345678. This connects SubFi Pay to your specific Discord server.
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Bot Token */}
        {step === 2 && (
          <div className="step-transition">
            <h1 className="login-title">Create a Discord Bot</h1>
            <p className="login-subtitle" style={{ marginBottom: 20 }}>
              SubFi Pay uses a bot you create to post payment menus and manage roles in your server.
            </p>

            <div style={{ background: 'var(--s2)', borderRadius: 10, padding: 14, marginBottom: 20, fontSize: 13, color: 'var(--t2)', lineHeight: 1.6, border: '1px solid var(--b1)' }}>
              <div style={{ fontWeight: 600, marginBottom: 8, color: 'var(--t1)' }}>How to create a bot and get the token:</div>
              <div>1. Go to <strong>discord.com/developers/applications</strong></div>
              <div>2. Click <strong>New Application</strong>, give it a name (e.g. "Payment Bot")</div>
              <div>3. Go to the <strong>Bot</strong> tab on the left, click <strong>Reset Token</strong></div>
              <div>4. <strong>Copy the token</strong> and paste it below</div>
              <div style={{ marginTop: 8, padding: '8px 0', borderTop: '1px solid var(--b1)' }}>
                <div style={{ fontWeight: 600, color: 'var(--t1)', marginBottom: 4 }}>Then invite the bot to your server:</div>
                <div>5. Go to the <strong>OAuth2</strong> tab, then <strong>URL Generator</strong></div>
                <div>6. Check <strong>bot</strong> under Scopes</div>
                <div>7. Check <strong>Manage Roles</strong> and <strong>Send Messages</strong> under Bot Permissions</div>
                <div>8. Copy the generated URL and open it to invite the bot</div>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Discord Bot Token</label>
              <div style={{ position: 'relative' }}>
                <input
                  className={inputClass('botToken', tokenErr)}
                  type={showToken ? 'text' : 'password'}
                  value={botToken}
                  onChange={(e) => setBotToken(e.target.value)}
                  onBlur={() => touch('botToken')}
                  placeholder="Paste your bot token here"
                  style={{ fontFamily: 'monospace', paddingRight: 60 }}
                  autoFocus
                />
                <button
                  type="button"
                  className="token-toggle"
                  onClick={() => setShowToken(!showToken)}
                >
                  {showToken ? 'Hide' : 'Show'}
                </button>
              </div>
              {touched.botToken && tokenErr && (
                <div className="form-hint error">{tokenErr}</div>
              )}
              {touched.botToken && !tokenErr && botToken && (
                <div className="form-hint success">Valid token length</div>
              )}
              <div className="form-hint" style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 4 }}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 12, height: 12, flexShrink: 0 }}>
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                </svg>
                Your token is encrypted with AES-256-GCM before storage. We never store it in plain text.
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Solana Wallet */}
        {step === 3 && (
          <div className="step-transition">
            <h1 className="login-title">Where Should Payments Go?</h1>
            <p className="login-subtitle" style={{ marginBottom: 20 }}>
              When someone pays, the crypto is sent to a temporary wallet, then automatically forwarded to yours.
            </p>

            <div style={{ background: 'var(--s2)', borderRadius: 10, padding: 14, marginBottom: 20, fontSize: 13, color: 'var(--t2)', lineHeight: 1.6, border: '1px solid var(--b1)' }}>
              <div style={{ fontWeight: 600, marginBottom: 8, color: 'var(--t1)' }}>What is a Solana wallet address?</div>
              <div>This is the public address of your Solana wallet where you want to receive payments. You can get one from:</div>
              <div style={{ marginTop: 6 }}>
                <div><strong>Phantom</strong> — phantom.app (most popular, browser extension + mobile)</div>
                <div><strong>Solflare</strong> — solflare.com (web + mobile)</div>
              </div>
              <div style={{ marginTop: 8 }}>
                Open your wallet app, tap <strong>Receive</strong> or <strong>Deposit</strong>, and copy the address. It looks like a long string of random letters and numbers.
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Your Solana Wallet Address</label>
              <input
                className={inputClass('walletSolana', walletErr)}
                value={walletSolana}
                onChange={(e) => setWalletSolana(e.target.value)}
                onBlur={() => touch('walletSolana')}
                placeholder="e.g. 7xKXtg2CW87d97TXJSDpbD5jBkheTqA83TZRuJosgAsU"
                style={{ fontFamily: 'monospace' }}
                autoFocus
              />
              {touched.walletSolana && walletErr && (
                <div className="form-hint error">{walletErr}</div>
              )}
              {touched.walletSolana && !walletErr && walletSolana && (
                <div className="form-hint success">Valid wallet address</div>
              )}
              <div className="form-hint" style={{ marginTop: 6 }}>
                This is your personal wallet — not an exchange deposit address. You'll be able to change it later in Settings.
              </div>
            </div>
          </div>
        )}

        {/* Step 4: Review */}
        {step === 4 && (
          <div className="step-transition">
            <h1 className="login-title">Review &amp; Create</h1>
            <p className="login-subtitle" style={{ marginBottom: 24 }}>
              Double-check everything looks right. You can change all of these later in Settings.
            </p>
            <div style={{ display: 'grid', gap: 12, marginBottom: 20 }}>
              <div className="review-row">
                <span className="review-label">Server Name</span>
                <span className="review-value">{name}</span>
              </div>
              <div className="review-row">
                <span className="review-label">Guild ID</span>
                <span className="review-value" style={{ fontFamily: 'monospace' }}>{guildId}</span>
              </div>
              <div className="review-row">
                <span className="review-label">Bot Token</span>
                <span className="review-value" style={{ fontFamily: 'monospace' }}>
                  {botToken ? `${botToken.slice(0, 8)}${'•'.repeat(20)}` : '—'}
                </span>
              </div>
              <div className="review-row">
                <span className="review-label">Solana Wallet</span>
                <span className="review-value" style={{ fontFamily: 'monospace' }}>
                  {walletSolana ? `${walletSolana.slice(0, 8)}...${walletSolana.slice(-4)}` : '—'}
                </span>
              </div>
            </div>

            <div style={{ background: 'var(--s2)', borderRadius: 10, padding: 14, marginBottom: 16, fontSize: 13, color: 'var(--t2)', lineHeight: 1.6, border: '1px solid var(--b1)' }}>
              <div style={{ fontWeight: 600, marginBottom: 4, color: 'var(--t1)' }}>What happens next?</div>
              <div>After creating your server, you'll land on the dashboard where you can create subscription plans, customize your payment embed, and test the <code style={{ background: 'var(--s1)', padding: '1px 4px', borderRadius: 3 }}>/menu</code> command in your Discord server.</div>
            </div>

            {error && <div className="form-error" style={{ marginBottom: 16 }}>{error}</div>}
          </div>
        )}

        {/* Navigation buttons */}
        <div style={{ display: 'flex', gap: 8, marginTop: 24 }}>
          {step > 0 && (
            <button
              className="btn-sm btn-ghost"
              style={{ flex: 1, padding: 12, fontSize: 14 }}
              onClick={() => setStep(step - 1)}
              disabled={creating}
            >
              Back
            </button>
          )}
          {step < 4 ? (
            <>
              {canSkip && (
                <button
                  className="btn-sm btn-ghost"
                  style={{ flex: 1, padding: 12, fontSize: 14, color: 'var(--t3)' }}
                  onClick={handleSkip}
                >
                  Skip, I'll do this later
                </button>
              )}
              <button
                className="btn-sm btn-primary"
                style={{ flex: 1, padding: 12, fontSize: 14 }}
                onClick={() => setStep(step + 1)}
                disabled={!canNext()}
              >
                Next
              </button>
            </>
          ) : (
            <button
              className="btn-sm btn-primary"
              style={{ flex: 1, padding: 12, fontSize: 14 }}
              onClick={() => void handleCreate()}
              disabled={creating}
            >
              {creating ? 'Creating Server...' : 'Create Server'}
            </button>
          )}
        </div>

        {/* Quick-create shortcut — visible once name + guild ID are filled (steps 1+) */}
        {step >= 1 && step < 4 && name.trim() && !guildErr && guildId && (
          <button
            onClick={() => void handleQuickCreate()}
            disabled={creating}
            style={{
              marginTop: 12, width: '100%', padding: 10,
              background: 'transparent', border: '1px dashed var(--b2)', borderRadius: 8,
              color: 'var(--t3)', fontSize: 13, cursor: 'pointer',
              transition: 'border-color 0.15s, color 0.15s',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--violet)'; e.currentTarget.style.color = 'var(--t2)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--b2)'; e.currentTarget.style.color = 'var(--t3)'; }}
          >
            Skip setup — create server with just name &amp; ID
          </button>
        )}

        {/* Safety: if user already has a server, let them go to dashboard */}
        {hasServer && (
          <button
            onClick={() => { localStorage.removeItem(STORAGE_KEY); navigate('/'); }}
            style={{
              marginTop: 12, width: '100%', padding: 10,
              background: 'transparent', border: 'none',
              color: 'var(--violet)', fontSize: 13, cursor: 'pointer',
              textDecoration: 'underline',
            }}
          >
            You already have a server — go to dashboard
          </button>
        )}
      </div>
    </div>
  );
}
